/**
 * Workspace symbols — fuzzy-match every symbol across every file.
 * Returns SymbolInformation entries so the editor's ⌘T picker can navigate.
 */

import {
  SymbolKind as LspSymbolKind,
  type SymbolInformation,
} from "vscode-languageserver";
import type { SymbolTable, SymbolEntry } from "../workspace/symbol-table.js";
import { toLspRange } from "../lsp-types.js";

export function provideWorkspaceSymbols(
  symbols: SymbolTable,
  query: string
): SymbolInformation[] {
  const matcher = fuzzyMatcher(query);

  const out: SymbolInformation[] = [];
  for (const sym of symbols.all()) {
    if (!matcher(sym.qualifiedName)) continue;
    const span = sym.nameSpan ?? sym.declSpan;
    if (!sym.definingUri || !span) continue;
    out.push({
      name: sym.qualifiedName,
      kind: toLspKind(sym.kind),
      location: {
        uri: sym.definingUri,
        range: toLspRange(span),
      },
      containerName: sym.ownerName,
    });
  }
  return out;
}

function fuzzyMatcher(query: string): (name: string) => boolean {
  if (query === "") return () => true;
  const needle = query.toLowerCase();
  return (name: string) => {
    const hay = name.toLowerCase();
    let i = 0;
    for (const ch of hay) {
      if (ch === needle[i]) i++;
      if (i === needle.length) return true;
    }
    return false;
  };
}

function toLspKind(kind: SymbolEntry["kind"]): LspSymbolKind {
  switch (kind) {
    case "module":
      return LspSymbolKind.Module;
    case "surface":
      return LspSymbolKind.Namespace;
    case "interface":
      return LspSymbolKind.Interface;
    case "process":
      return LspSymbolKind.Function;
    case "subprocess":
      return LspSymbolKind.Method;
    case "view":
      return LspSymbolKind.Object;
    case "type":
      return LspSymbolKind.Class;
    case "aspect":
      return LspSymbolKind.EnumMember;
    case "reentry":
      return LspSymbolKind.Key;
  }
}
